/**
 * FeatureAccumulator — a client-side store of GeoJSON features keyed by a stable
 * id, used so map panning appends data instead of replacing it.
 *
 * It is deliberately rendering-model-agnostic: layers that draw HTML markers and
 * layers that drive a MapLibre GeoJSON source both merge into it the same way.
 *
 * Correctness rules enforced by callers:
 *  - Only individual (non-aggregated) responses are merged; aggregated cluster
 *    responses have viewport-partial counts and are never accumulated.
 *  - The `datasetKey` fingerprints everything except the bounding box (mode +
 *    active filters). When it changes the store is reset so the next render is a
 *    clean replace.
 */

import { centroidOfGeometry } from './coords';

function withinBounds(bounds, lngLat) {
    if (!bounds || !lngLat) {
        return true;
    }
    return lngLat[0] >= bounds.getWest()
        && lngLat[0] <= bounds.getEast()
        && lngLat[1] >= bounds.getSouth()
        && lngLat[1] <= bounds.getNorth();
}

export default class FeatureAccumulator {
    constructor() {
        this._key = null;
        this._store = new Map(); // id -> { feature, lastSeen }
        this._tick = 0;
    }

    getKey() {
        return this._key;
    }

    size() {
        return this._store.size;
    }

    isEmpty() {
        return this._store.size === 0;
    }

    reset(key) {
        this._store.clear();
        this._key = key === undefined ? null : key;
        this._tick = 0;
    }

    /**
     * Merge a batch of features. If `key` differs from the stored dataset key the
     * store is reset first (a fresh dataset). `keyFn(feature)` must return a
     * stable unique id; features without one are skipped.
     *
     * @returns {{added:number, refreshed:number, replaced:boolean}}
     */
    merge(key, features, keyFn) {
        let replaced = false;
        if (key !== this._key) {
            this.reset(key);
            replaced = true;
        }

        this._tick += 1;
        let added = 0;
        let refreshed = 0;

        (features || []).forEach((feature) => {
            const raw = keyFn(feature);
            if (raw == null || raw === '') {
                return;
            }
            const id = String(raw);
            if (this._store.has(id)) {
                refreshed += 1;
            } else {
                added += 1;
            }
            this._store.set(id, { feature: feature, lastSeen: this._tick });
        });

        return { added: added, refreshed: refreshed, replaced: replaced };
    }

    /**
     * Drop features outside `keepBounds`, then — if still over `maxCount` — drop
     * the least-recently-seen ones. Returns the removed ids.
     *
     * @param {maplibregl.LngLatBounds|null} keepBounds
     * @param {number} maxCount
     * @returns {string[]}
     */
    evict(keepBounds, maxCount) {
        const removed = [];

        if (keepBounds) {
            this._store.forEach((entry, id) => {
                const centroid = centroidOfGeometry(entry.feature && entry.feature.geometry);
                if (centroid && !withinBounds(keepBounds, centroid)) {
                    removed.push(id);
                }
            });
            removed.forEach((id) => this._store.delete(id));
        }

        if (maxCount && this._store.size > maxCount) {
            const entries = Array.from(this._store.entries())
                .sort((a, b) => a[1].lastSeen - b[1].lastSeen);
            const overflow = this._store.size - maxCount;
            for (let i = 0; i < overflow; i += 1) {
                this._store.delete(entries[i][0]);
                removed.push(entries[i][0]);
            }
        }

        return removed;
    }

    ids() {
        return new Set(this._store.keys());
    }

    values() {
        const out = [];
        this._store.forEach((entry) => out.push(entry.feature));
        return out;
    }

    featureCollection() {
        return { type: 'FeatureCollection', features: this.values() };
    }
}
